import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { Show } from '../../../../../../shared/Show';
import { AddContactForm } from '../../../shared/AddContactForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStamp } from '@fortawesome/pro-regular-svg-icons';
import { WizardFormProps } from '../../../shared/WizardFormProps';
import * as yup from 'yup';
import { RateField } from './RateField';
import { DEFAULT_RATE_VALUE } from '../../DEFAULT_RATE_VALUE';
import { CreateSigningAppointmentFormData } from '../../../04_confirmationStep/convertToCreateSigningAppointmentRequest';
import { IconPlusOutline } from '@zillow/constellation-icons';
import { css } from '@/styled-system/css';
import { Button } from '@zillow/constellation-10';

const formTitle = 'Add Notary';

export const NotaryForm: React.FC<WizardFormProps> = ({
  advance,
  updatedWizardFormData,
  setUpdatedWizardFormData,
  children,
  isDigSync,
}) => {
  const [showInitialScreen, setShowInitialScreen] = useState<boolean>(true);

  const handleOnThrashIconClick = () => {
    // reset notary values and show initial screen;
    setShowInitialScreen(true);
    setUpdatedWizardFormData((prevState) => ({
      ...prevState,
      notary: undefined,
    }));
  };
  return (
    <Formik
      initialValues={buildInitialFormData(updatedWizardFormData)}
      validationSchema={isDigSync ? notaryDigSyncValidationSchema : notaryValidationSchema}
      onSubmit={(values: INotaryFormData) => {
        if (!values.notary) {
          values.rateCents = undefined;
        }
        setUpdatedWizardFormData((prevState) => ({
          ...prevState,
          ...values,
        }));
        advance();
      }}
      validateOnMount={true}
    >
      {({ values }) => (
        <Form className={css({ marginTop: '12' })}>
          <h5 className={css({ marginBottom: '16', fontSize: 'base', fontWeight: 'medium' })}>Notary</h5>
          <Show when={!updatedWizardFormData.isSignatureSyncAppointment}>
            <Show
              when={showInitialScreen}
              fallback={
                <AddContactForm
                  isSigner={false}
                  title={formTitle}
                  notary={values.notary}
                  onTrashIconClick={handleOnThrashIconClick}
                  emailRequired={!!updatedWizardFormData.isSignatureSyncAppointment}
                  isLegacy={!updatedWizardFormData.isDigitalScheduling}
                >
                  <RateField wizardFormData={updatedWizardFormData} />
                </AddContactForm>
              }
            >
              <section
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
                data-test-id="no-notaries-section"
              >
                <FontAwesomeIcon className={css({ height: '48', width: '48', color: '#4A4D7D' })} icon={faStamp} />
                <span className={css({ fontSize: 'base', fontWeight: '500', color: '#161616' })}>No Notaries</span>
              </section>
            </Show>

            <section
              className={css({ marginTop: '16', display: 'flex', width: '256', justifyContent: 'space-between' })}
            >
              <Button
                tone={'brand'}
                emphasis={'filled'}
                size="sm"
                icon={<IconPlusOutline />}
                iconPosition="start"
                onClick={() => setShowInitialScreen(false)}
              >
                <p>Add New</p>
              </Button>
            </section>
          </Show>
          {children}
        </Form>
      )}
    </Formik>
  );
};

export type INotaryFormData = Pick<CreateSigningAppointmentFormData, 'notary' | 'rateCents'>;

const buildInitialFormData = (wizardFormData: CreateSigningAppointmentFormData): INotaryFormData => {
  return {
    notary: wizardFormData.notary,
    rateCents: wizardFormData.rateCents ?? DEFAULT_RATE_VALUE,
  };
};

export const notaryValidationSchema = yup.object().shape({
  notary: yup.object().shape({
    firstName: yup.string().required('Notary First Name is required').typeError('Notary First Name is required'),
    lastName: yup.string().required('Notary Last Name is required').typeError('Notary Last Name is required'),
  }),
});

export const notaryDigSyncValidationSchema = yup.object().shape({
  notary: yup.object().shape({
    firstName: yup.string().nullable(),
    lastName: yup.string().nullable(),
    email: yup.string().nullable(),
  }),
});
